import { PageBlock, PageDesign, PageSocial } from "../../../types";

export type TemplateCategory =
  | "creator"
  | "business"
  | "shop"
  | "events"
  | "promotions";

export interface PageTemplate {
  id: string;
  name: string;
  blurb: string; // one-line description of who it's for
  category: TemplateCategory;
  data: {
    title: string;
    bio: string;
    avatar_color: string;
    avatar_image?: string;
    blocks: PageBlock[];
    socials: PageSocial[];
    design: PageDesign;
  };
}

export const CATEGORIES: { key: "all" | TemplateCategory; label: string }[] = [
  { key: "all", label: "All templates" },
  { key: "creator", label: "Creators & influencers" },
  { key: "business", label: "Business & professional" },
  { key: "shop", label: "Shops & restaurants" },
  { key: "events", label: "Events & launches" },
  { key: "promotions", label: "Promotions & sales" },
];

const BASE: PageDesign = {
  theme: "light",
  bg_type: "gradient",
  bg_color: "#ffffff",
  bg_gradient_from: "#eef2ff",
  bg_gradient_to: "#fdf4ff",
  text_color: "#18181b",
  font: "Inter",
  button_shape: "rounded",
  button_shadow: "soft",
  button_color: "#6366f1",
  button_text_color: "#ffffff",
  social_placement: "top",
};
const design = (p: Partial<PageDesign>): PageDesign => ({ ...BASE, ...p });

const link = (label: string, url = "https://example.com", description?: string): PageBlock =>
  ({ type: "link", label, url, ...(description ? { description } : {}) });
// A standout, accented link (renders with the featured highlight).
const flink = (label: string, url = "https://example.com", description?: string): PageBlock =>
  ({ type: "link", label, url, featured: true, ...(description ? { description } : {}) });
const text = (t: string): PageBlock => ({ type: "text", text: t });
const header = (t: string): PageBlock => ({ type: "header", text: t });
const divider = (): PageBlock => ({ type: "divider" });
const image = (image_url: string, url = ""): PageBlock => ({ type: "image", image_url, url });

export const TEMPLATES: PageTemplate[] = [
  // ---- Creators & influencers ------------------------------------------
  {
    id: "creator",
    name: "Content Creator",
    blurb: "For influencers sharing videos, drops & collabs",
    category: "creator",
    data: {
      title: "Maya Chen",
      bio: "Daily content on growth & lifestyle ✨",
      avatar_color: "#6366f1",
      blocks: [
        link("▶  Watch my latest video"),
        link("Join my free 5-day challenge"),
        link("Shop my favorites", "https://example.com", "Everything I actually use"),
      ],
      socials: [
        { platform: "instagram", url: "https://instagram.com" },
        { platform: "tiktok", url: "https://tiktok.com" },
        { platform: "youtube", url: "https://youtube.com" },
      ],
      design: design({
        font: "Poppins",
        bg_gradient_from: "#e0e7ff",
        bg_gradient_to: "#fae8ff",
        button_color: "#6366f1",
        button_shape: "pill",
        button_shadow: "soft",
      }),
    },
  },
  {
    id: "podcast",
    name: "Podcast Show",
    blurb: "Send listeners to every player in one tap",
    category: "creator",
    data: {
      title: "The Honest Hour",
      bio: "Real talk on business & life · New episodes every Tuesday",
      avatar_color: "#06b6d4",
      blocks: [
        link("🎧  Listen on Spotify"),
        link("Listen on Apple Podcasts"),
        link("Watch on YouTube"),
        text("Join 50,000+ weekly listeners."),
      ],
      socials: [
        { platform: "spotify", url: "https://spotify.com" },
        { platform: "youtube", url: "https://youtube.com" },
        { platform: "instagram", url: "https://instagram.com" },
      ],
      design: design({
        bg_type: "color",
        bg_color: "#0f172a",
        text_color: "#e2e8f0",
        font: "Montserrat",
        button_color: "#22d3ee",
        button_text_color: "#042f2e",
        button_shape: "rounded",
        button_shadow: "none",
      }),
    },
  },
  {
    id: "musician",
    name: "Musician & Band",
    blurb: "Promote a release, tour dates and merch",
    category: "creator",
    data: {
      title: "NOVA",
      bio: "New single ‘Midnight’ — out now",
      avatar_color: "#ec4899",
      blocks: [
        image("https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=600"),
        link("Stream ‘Midnight’"),
        link("Tour dates & tickets"),
        link("Official merch store"),
      ],
      socials: [
        { platform: "spotify", url: "https://spotify.com" },
        { platform: "instagram", url: "https://instagram.com" },
        { platform: "youtube", url: "https://youtube.com" },
        { platform: "tiktok", url: "https://tiktok.com" },
      ],
      design: design({
        bg_type: "color",
        bg_color: "#18181b",
        text_color: "#fafafa",
        font: "Montserrat",
        button_color: "#ec4899",
        button_shape: "pill",
        button_shadow: "hard",
      }),
    },
  },
  {
    id: "fitness-coach",
    name: "Fitness Coach",
    blurb: "Sell programs and book training clients",
    category: "creator",
    data: {
      title: "Coach Tyler",
      bio: "Helping you get stronger 💪 Online coaching & plans",
      avatar_color: "#84cc16",
      blocks: [
        link("Join my 12-week program"),
        link("Download free workout guide"),
        link("Book a 1:1 consult"),
        text("100+ client transformations and counting."),
      ],
      socials: [
        { platform: "instagram", url: "https://instagram.com" },
        { platform: "youtube", url: "https://youtube.com" },
        { platform: "tiktok", url: "https://tiktok.com" },
      ],
      design: design({
        bg_type: "color",
        bg_color: "#0a0a0a",
        text_color: "#fafafa",
        font: "Montserrat",
        button_color: "#a3e635",
        button_text_color: "#0a0a0a",
        button_shape: "square",
        button_shadow: "hard",
      }),
    },
  },
  {
    id: "author",
    name: "Author & Writer",
    blurb: "Sell books and grow your reader list",
    category: "creator",
    data: {
      title: "Eleanor Hart",
      bio: "Novelist · author of The Saltwater Trilogy",
      avatar_color: "#1c1917",
      blocks: [
        link("Buy my new book"),
        link("Read a free excerpt"),
        link("Join my reader newsletter"),
        text("Signed copies available this month."),
      ],
      socials: [
        { platform: "instagram", url: "https://instagram.com" },
        { platform: "x", url: "https://x.com" },
        { platform: "website", url: "https://example.com" },
        { platform: "email", url: "mailto:hello@example.com" },
      ],
      design: design({
        bg_type: "color",
        bg_color: "#faf6f0",
        text_color: "#1c1917",
        font: "Playfair Display",
        button_color: "#1c1917",
        button_text_color: "#faf6f0",
        button_shape: "rounded",
        button_shadow: "none",
      }),
    },
  },

  // ---- Business & professional -----------------------------------------
  {
    id: "freelancer",
    name: "Freelancer Portfolio",
    blurb: "Show your work and take on new projects",
    category: "business",
    data: {
      title: "Sam Park",
      bio: "Brand & web designer · open for work",
      avatar_color: "#6366f1",
      blocks: [
        link("View my portfolio"),
        link("Services & pricing"),
        link("Start a project", "https://example.com", "Tell me what you need"),
      ],
      socials: [
        { platform: "website", url: "https://example.com" },
        { platform: "linkedin", url: "https://linkedin.com" },
        { platform: "instagram", url: "https://instagram.com" },
        { platform: "email", url: "mailto:hi@example.com" },
      ],
      design: design({
        bg_type: "gradient",
        bg_gradient_from: "#f1f5f9",
        bg_gradient_to: "#eef2ff",
        text_color: "#0f172a",
        font: "Inter",
        button_color: "#4f46e5",
        button_shape: "rounded",
        button_shadow: "soft",
      }),
    },
  },
  {
    id: "consultant",
    name: "Consultant & Coach",
    blurb: "Book discovery calls and present services",
    category: "business",
    data: {
      title: "Dr. Amara Osei",
      bio: "Business strategist · helping founders scale",
      avatar_color: "#1e3a8a",
      blocks: [
        link("Book a free strategy call"),
        link("How I can help"),
        link("Client case studies"),
        text("Trusted by 200+ founders and teams."),
      ],
      socials: [
        { platform: "linkedin", url: "https://linkedin.com" },
        { platform: "website", url: "https://example.com" },
        { platform: "email", url: "mailto:amara@example.com" },
      ],
      design: design({
        bg_type: "color",
        bg_color: "#f8fafc",
        text_color: "#0f172a",
        font: "Inter",
        button_color: "#1e3a8a",
        button_shape: "rounded",
        button_shadow: "soft",
      }),
    },
  },
  {
    id: "business-card",
    name: "Digital Business Card",
    blurb: "A clean, tappable contact card",
    category: "business",
    data: {
      title: "Jordan Lee",
      bio: "Product Designer · San Francisco",
      avatar_color: "#0ea5e9",
      blocks: [
        link("Save my contact"),
        link("Email me", "mailto:jordan@example.com"),
        link("Visit my website"),
        link("Book a call"),
      ],
      socials: [
        { platform: "linkedin", url: "https://linkedin.com" },
        { platform: "email", url: "mailto:jordan@example.com" },
        { platform: "website", url: "https://example.com" },
        { platform: "x", url: "https://x.com" },
      ],
      design: design({
        bg_type: "color",
        bg_color: "#ffffff",
        text_color: "#0f172a",
        font: "Inter",
        button_color: "#0f172a",
        button_shape: "pill",
        button_shadow: "soft",
      }),
    },
  },
  {
    id: "real-estate",
    name: "Real Estate Agent",
    blurb: "Share listings and capture buyer leads",
    category: "business",
    data: {
      title: "Olivia Bennett",
      bio: "Realtor® · helping you find home in Austin",
      avatar_color: "#b45309",
      blocks: [
        image("https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=600"),
        link("Browse my listings"),
        link("Schedule a viewing"),
        link("Get a free home valuation"),
      ],
      socials: [
        { platform: "instagram", url: "https://instagram.com" },
        { platform: "facebook", url: "https://facebook.com" },
        { platform: "email", url: "mailto:olivia@example.com" },
        { platform: "website", url: "https://example.com" },
      ],
      design: design({
        bg_type: "color",
        bg_color: "#1e293b",
        text_color: "#f1f5f9",
        font: "Playfair Display",
        button_color: "#c8a45c",
        button_text_color: "#1e293b",
        button_shape: "rounded",
        button_shadow: "none",
      }),
    },
  },

  // ---- Shops & restaurants ---------------------------------------------
  {
    id: "online-store",
    name: "Online Store",
    blurb: "Funnel followers to your products",
    category: "shop",
    data: {
      title: "Lumen Goods",
      bio: "Minimal essentials for everyday ✦",
      avatar_color: "#18181b",
      blocks: [
        image("https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=600"),
        link("Shop new arrivals"),
        link("Shop bestsellers"),
        link("Get 10% off your first order", "https://example.com", "Use code WELCOME10"),
      ],
      socials: [
        { platform: "instagram", url: "https://instagram.com" },
        { platform: "tiktok", url: "https://tiktok.com" },
        { platform: "email", url: "mailto:hello@lumengoods.com" },
      ],
      design: design({
        bg_type: "color",
        bg_color: "#ffffff",
        text_color: "#18181b",
        font: "Inter",
        button_color: "#18181b",
        button_shape: "square",
        button_shadow: "soft",
      }),
    },
  },
  {
    id: "restaurant",
    name: "Restaurant & Café",
    blurb: "Menu, reservations and ordering in one place",
    category: "shop",
    data: {
      title: "Olive & Vine",
      bio: "Mediterranean kitchen · Downtown",
      avatar_color: "#9a3412",
      blocks: [
        link("View our menu"),
        link("Reserve a table"),
        link("Order delivery"),
        text("Open daily · 11am – 11pm"),
      ],
      socials: [
        { platform: "instagram", url: "https://instagram.com" },
        { platform: "facebook", url: "https://facebook.com" },
        { platform: "email", url: "mailto:eat@oliveandvine.com" },
      ],
      design: design({
        bg_type: "color",
        bg_color: "#fdf6ec",
        text_color: "#3f2d1c",
        font: "Playfair Display",
        button_color: "#9a3412",
        button_shape: "rounded",
        button_shadow: "soft",
      }),
    },
  },

  // ---- Events & launches -----------------------------------------------
  {
    id: "conference",
    name: "Event & Conference",
    blurb: "Drive ticket sales and share the schedule",
    category: "events",
    data: {
      title: "DesignConf 2026",
      bio: "The conference for product builders · June 14–16, Berlin",
      avatar_color: "#7c3aed",
      blocks: [
        link("Get your ticket"),
        link("View the schedule"),
        link("Meet the speakers"),
        text("Early-bird pricing ends June 1."),
      ],
      socials: [
        { platform: "x", url: "https://x.com" },
        { platform: "linkedin", url: "https://linkedin.com" },
        { platform: "instagram", url: "https://instagram.com" },
        { platform: "website", url: "https://example.com" },
      ],
      design: design({
        bg_type: "gradient",
        bg_gradient_from: "#312e81",
        bg_gradient_to: "#6d28d9",
        text_color: "#f5f3ff",
        font: "Poppins",
        button_color: "#a78bfa",
        button_text_color: "#1e1b4b",
        button_shape: "pill",
        button_shadow: "soft",
      }),
    },
  },
  {
    id: "product-launch",
    name: "Product Launch",
    blurb: "Build a waitlist for your new product",
    category: "events",
    data: {
      title: "Introducing Halo",
      bio: "The smartest way to plan your day. Now in early access.",
      avatar_color: "#6366f1",
      blocks: [
        link("Join the waitlist"),
        link("Watch the 2-min demo"),
        link("Read the docs"),
      ],
      socials: [
        { platform: "x", url: "https://x.com" },
        { platform: "website", url: "https://example.com" },
        { platform: "youtube", url: "https://youtube.com" },
      ],
      design: design({
        bg_type: "color",
        bg_color: "#09090b",
        text_color: "#fafafa",
        font: "Inter",
        button_color: "#6366f1",
        button_shape: "rounded",
        button_shadow: "soft",
      }),
    },
  },
  {
    id: "wedding",
    name: "Wedding",
    blurb: "RSVPs, details and registry for your big day",
    category: "events",
    data: {
      title: "Emma & James",
      bio: "We're getting married! · September 12, 2026",
      avatar_color: "#be185d",
      blocks: [
        link("RSVP"),
        link("Event details & schedule"),
        link("Gift registry"),
        text("Tuscany, Italy · We can't wait to celebrate with you."),
      ],
      socials: [{ platform: "email", url: "mailto:emmaandjames@example.com" }],
      design: design({
        bg_type: "gradient",
        bg_gradient_from: "#fdf2f8",
        bg_gradient_to: "#faf5ff",
        text_color: "#831843",
        font: "Playfair Display",
        button_color: "#be185d",
        button_shape: "pill",
        button_shadow: "none",
      }),
    },
  },

  // ---- Promotions & sales ----------------------------------------------
  {
    id: "flash-sale",
    name: "Flash Sale",
    blurb: "Urgent, high-energy page for limited offers",
    category: "promotions",
    data: {
      title: "Flash Sale — 48 Hours",
      bio: "Up to 60% off everything 🔥",
      avatar_color: "#f43f5e",
      blocks: [
        link("Shop the sale"),
        link("Unlock an extra 15% off", "https://example.com", "Use code FLASH15"),
        text("Ends Sunday at midnight — don't miss it."),
      ],
      socials: [
        { platform: "instagram", url: "https://instagram.com" },
        { platform: "tiktok", url: "https://tiktok.com" },
      ],
      design: design({
        bg_type: "gradient",
        bg_gradient_from: "#fb7185",
        bg_gradient_to: "#f43f5e",
        text_color: "#ffffff",
        font: "Poppins",
        button_color: "#ffffff",
        button_text_color: "#be123c",
        button_shape: "pill",
        button_shadow: "hard",
      }),
    },
  },
  {
    id: "black-friday",
    name: "Black Friday",
    blurb: "Bold seasonal page for your biggest deals",
    category: "promotions",
    data: {
      title: "Black Friday",
      bio: "Our biggest deals of the year are live",
      avatar_color: "#facc15",
      blocks: [
        link("Shop doorbuster deals"),
        link("Early access for members"),
        link("Free shipping over $50"),
        text("Deals drop every hour while stock lasts."),
      ],
      socials: [
        { platform: "instagram", url: "https://instagram.com" },
        { platform: "email", url: "mailto:deals@example.com" },
      ],
      design: design({
        bg_type: "color",
        bg_color: "#0a0a0a",
        text_color: "#fafafa",
        font: "Montserrat",
        button_color: "#facc15",
        button_text_color: "#0a0a0a",
        button_shape: "square",
        button_shadow: "hard",
      }),
    },
  },

  // ---- More creators ---------------------------------------------------
  {
    id: "streamer",
    name: "Live Streamer",
    blurb: "For Twitch/YouTube gamers — push viewers to your stream",
    category: "creator",
    data: {
      title: "ByteRaider",
      bio: "Variety streamer · live most nights 🎮",
      avatar_color: "#a855f7",
      blocks: [
        flink("🔴  Watch me live"),
        header("Channels"),
        link("Twitch"),
        link("YouTube VODs"),
        link("Discord community"),
        divider(),
        link("Support the stream", "https://example.com", "Donations & subs"),
      ],
      socials: [
        { platform: "youtube", url: "https://youtube.com" },
        { platform: "instagram", url: "https://instagram.com" },
        { platform: "tiktok", url: "https://tiktok.com" },
      ],
      design: design({
        bg_type: "color",
        bg_color: "#0b0410",
        text_color: "#f5f3ff",
        font: "Montserrat",
        button_color: "#a855f7",
        button_shape: "rounded",
        button_shadow: "hard",
      }),
    },
  },
  {
    id: "newsletter",
    name: "Newsletter Writer",
    blurb: "Convert visitors into subscribers",
    category: "creator",
    data: {
      title: "The Weekly Byte",
      bio: "One sharp idea on tech & creativity, every Friday",
      avatar_color: "#0ea5e9",
      blocks: [
        flink("Subscribe free", "https://example.com", "Join 12,000+ readers"),
        link("Read past issues"),
        link("Recommend the newsletter"),
        text("No spam. Unsubscribe anytime."),
      ],
      socials: [
        { platform: "x", url: "https://x.com" },
        { platform: "linkedin", url: "https://linkedin.com" },
        { platform: "website", url: "https://example.com" },
      ],
      design: design({
        bg_type: "gradient",
        bg_gradient_from: "#ecfeff",
        bg_gradient_to: "#eff6ff",
        text_color: "#0c4a6e",
        font: "Inter",
        button_color: "#0ea5e9",
        button_shape: "rounded",
        button_shadow: "soft",
      }),
    },
  },
  {
    id: "photographer",
    name: "Photographer",
    blurb: "Showcase work and take booking enquiries",
    category: "creator",
    data: {
      title: "Lina Marworks",
      bio: "Portrait & wedding photography · Lisbon",
      avatar_color: "#78716c",
      blocks: [
        image("https://images.unsplash.com/photo-1519741497674-611481863552?w=600"),
        flink("Book a session"),
        link("View the portfolio"),
        link("Pricing & packages"),
      ],
      socials: [
        { platform: "instagram", url: "https://instagram.com" },
        { platform: "website", url: "https://example.com" },
        { platform: "email", url: "mailto:hello@example.com" },
      ],
      design: design({
        bg_type: "color",
        bg_color: "#1c1917",
        text_color: "#fafaf9",
        font: "Playfair Display",
        button_color: "#e7e5e4",
        button_text_color: "#1c1917",
        button_shape: "square",
        button_shadow: "none",
      }),
    },
  },

  // ---- More business ---------------------------------------------------
  {
    id: "saas-startup",
    name: "SaaS Startup",
    blurb: "Drive free-trial signups with a clear hierarchy",
    category: "business",
    data: {
      title: "Flowdesk",
      bio: "The workspace that runs itself.",
      avatar_color: "#4f46e5",
      blocks: [
        flink("Start free trial", "https://example.com", "No card required"),
        header("Product"),
        link("Features"),
        link("Pricing"),
        link("Watch the demo"),
        header("Company"),
        link("About us"),
        link("Careers — we're hiring"),
        divider(),
        link("Talk to sales"),
      ],
      socials: [
        { platform: "x", url: "https://x.com" },
        { platform: "linkedin", url: "https://linkedin.com" },
        { platform: "youtube", url: "https://youtube.com" },
      ],
      design: design({
        bg_type: "gradient",
        bg_gradient_from: "#eef2ff",
        bg_gradient_to: "#f5f3ff",
        text_color: "#1e1b4b",
        font: "Inter",
        button_color: "#4f46e5",
        button_shape: "rounded",
        button_shadow: "soft",
      }),
    },
  },
  {
    id: "local-service",
    name: "Local Business",
    blurb: "Bookings & calls for salons, clinics, trades",
    category: "business",
    data: {
      title: "Bloom Hair Studio",
      bio: "Modern cuts & color · Brooklyn",
      avatar_color: "#db2777",
      blocks: [
        flink("Book an appointment"),
        link("Call us", "tel:+15551234567"),
        link("See our services & prices"),
        link("Find us on the map"),
        text("Tue–Sat · 9am–7pm"),
      ],
      socials: [
        { platform: "instagram", url: "https://instagram.com" },
        { platform: "facebook", url: "https://facebook.com" },
      ],
      design: design({
        bg_type: "gradient",
        bg_gradient_from: "#fdf2f8",
        bg_gradient_to: "#fff1f2",
        text_color: "#831843",
        font: "Poppins",
        button_color: "#db2777",
        button_shape: "pill",
        button_shadow: "soft",
      }),
    },
  },
  {
    id: "nonprofit",
    name: "Nonprofit",
    blurb: "Lead with donations and ways to help",
    category: "business",
    data: {
      title: "Green Roots",
      bio: "Planting a greener tomorrow, today 🌱",
      avatar_color: "#16a34a",
      blocks: [
        flink("Donate", "https://example.com", "Every dollar plants a tree"),
        header("Get involved"),
        link("Volunteer with us"),
        link("Fundraise for the cause"),
        link("Our 2026 impact report"),
        divider(),
        link("Partner with us"),
      ],
      socials: [
        { platform: "instagram", url: "https://instagram.com" },
        { platform: "facebook", url: "https://facebook.com" },
        { platform: "email", url: "mailto:hello@example.org" },
      ],
      design: design({
        bg_type: "color",
        bg_color: "#f0fdf4",
        text_color: "#14532d",
        font: "Inter",
        button_color: "#16a34a",
        button_shape: "rounded",
        button_shadow: "soft",
      }),
    },
  },

  // ---- More shops ------------------------------------------------------
  {
    id: "beauty-brand",
    name: "Beauty Brand",
    blurb: "Clean, elegant page for cosmetics & skincare",
    category: "shop",
    data: {
      title: "Lueur",
      bio: "Clean skincare that glows ✨",
      avatar_color: "#f59e0b",
      blocks: [
        flink("Shop bestsellers"),
        link("New: Radiance Serum", "https://example.com", "Sold out twice — back in stock"),
        link("Take the skin quiz"),
        link("Find your shade"),
      ],
      socials: [
        { platform: "instagram", url: "https://instagram.com" },
        { platform: "tiktok", url: "https://tiktok.com" },
      ],
      design: design({
        bg_type: "gradient",
        bg_gradient_from: "#fffbeb",
        bg_gradient_to: "#fdf2f8",
        text_color: "#78350f",
        font: "Playfair Display",
        button_color: "#b45309",
        button_shape: "pill",
        button_shadow: "soft",
      }),
    },
  },
  {
    id: "course-creator",
    name: "Course Creator",
    blurb: "Sell an online course or cohort",
    category: "shop",
    data: {
      title: "Ship It with Sara",
      bio: "Learn to launch your first product in 30 days",
      avatar_color: "#7c3aed",
      blocks: [
        flink("Enroll now", "https://example.com", "Next cohort starts soon"),
        header("Free stuff"),
        link("Free starter guide"),
        link("Watch a sample lesson"),
        header("Course"),
        link("Curriculum & syllabus"),
        link("Student reviews"),
        divider(),
        link("Questions? Email me", "mailto:sara@example.com"),
      ],
      socials: [
        { platform: "youtube", url: "https://youtube.com" },
        { platform: "x", url: "https://x.com" },
        { platform: "linkedin", url: "https://linkedin.com" },
      ],
      design: design({
        bg_type: "gradient",
        bg_gradient_from: "#f5f3ff",
        bg_gradient_to: "#faf5ff",
        text_color: "#3b0764",
        font: "Poppins",
        button_color: "#7c3aed",
        button_shape: "rounded",
        button_shadow: "soft",
      }),
    },
  },

  // ---- More events & promos -------------------------------------------
  {
    id: "webinar",
    name: "Webinar & Workshop",
    blurb: "Register attendees and show the agenda",
    category: "events",
    data: {
      title: "Scaling to 10k Users",
      bio: "Free live workshop · Thursday, 6pm GMT",
      avatar_color: "#0891b2",
      blocks: [
        flink("Save my seat", "https://example.com", "Limited to 500 spots"),
        header("What we'll cover"),
        link("Full agenda"),
        link("Meet the host"),
        divider(),
        link("Add to calendar"),
        text("Can't make it live? Register and we'll send the replay."),
      ],
      socials: [
        { platform: "linkedin", url: "https://linkedin.com" },
        { platform: "x", url: "https://x.com" },
      ],
      design: design({
        bg_type: "color",
        bg_color: "#083344",
        text_color: "#ecfeff",
        font: "Inter",
        button_color: "#06b6d4",
        button_text_color: "#042f2e",
        button_shape: "rounded",
        button_shadow: "soft",
      }),
    },
  },
  {
    id: "product-drop",
    name: "Product Drop",
    blurb: "Hype a limited release with one clear CTA",
    category: "promotions",
    data: {
      title: "The Midnight Drop",
      bio: "Limited run · live Friday at 12:00 AM",
      avatar_color: "#e11d48",
      blocks: [
        flink("Shop the drop"),
        link("Get the drop reminder"),
        link("See the lookbook"),
        text("Once it's gone, it's gone."),
      ],
      socials: [
        { platform: "instagram", url: "https://instagram.com" },
        { platform: "tiktok", url: "https://tiktok.com" },
      ],
      design: design({
        bg_type: "color",
        bg_color: "#0a0a0a",
        text_color: "#fafafa",
        font: "Montserrat",
        button_color: "#e11d48",
        button_shape: "square",
        button_shadow: "hard",
      }),
    },
  },
  {
    id: "giveaway",
    name: "Giveaway",
    blurb: "Run a contest and grow your following",
    category: "promotions",
    data: {
      title: "Win a $500 Gift Card",
      bio: "One lucky winner · drawn at month end 🎁",
      avatar_color: "#9333ea",
      blocks: [
        flink("Enter to win", "https://example.com", "Takes 30 seconds"),
        header("Bonus entries"),
        link("Follow on Instagram"),
        link("Share with a friend"),
        divider(),
        link("Giveaway rules"),
      ],
      socials: [
        { platform: "instagram", url: "https://instagram.com" },
        { platform: "tiktok", url: "https://tiktok.com" },
      ],
      design: design({
        bg_type: "gradient",
        bg_gradient_from: "#f5f3ff",
        bg_gradient_to: "#fae8ff",
        text_color: "#581c87",
        font: "Poppins",
        button_color: "#9333ea",
        button_shape: "pill",
        button_shadow: "hard",
      }),
    },
  },
];
